import {
  AccessorialCode,
  DomainReference,
  Optional,
} from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';

import i18n from '@/i18n';

import { Shipment } from '../../shipment';
import { ShipmentStopType } from '../../shipment/ShipmentStopType';
import { ShipOrder } from '../../ShipOrder';
import { ShipOrderShippingPoint } from '../../ShipOrderShippingPoint';
import { ShippingPoint } from '../../ShippingPoint';
import { AddStopToShipmentPayload } from '../events/AddStopToShipmentPayload';
import { PlanningStatus } from '../PlanningStatus';
import { ProposedShipment } from '../ProposedShipment';
import { ProposedShipmentLineItem } from '../ProposedShipmentLineItem';
import { ResequenceState } from '../ResequenceState';
import { ValidShipOrder } from '../ValidShipOrder';
import { ValidShipOrderShippingPoint } from '../ValidShipOrderShippingPoint';

import { RoutePlanStop } from './RoutePlanStop';
import { RoutePlanTransportSegment } from './RoutePlanTransportSegment';

const DEFAULT_REMOVE_SHIP_ORDER_OPTIONS = {
  removeAssociatedStops: false,
};

/**
 * Generate a predicate that can be used in `Array.filter` to find stops that
 * can fulfill the given `ShipOrderShippingPoint`. A stop can fulfill a shipping
 * point if the stop and the shipping point:
 *
 * * have the same type (pick/drop)
 * * have the same address
 * * have overlapping dates (the appointment will be narrowed to the intersection)
 *
 * @param shipOrderShippingPoint
 * @returns a predicate that can be used in `Array.filter` to find stops that can fulfill the given `ShipOrderShippingPoint`
 */
const byStopsThatCouldFulfillShipOrderShippingPoint = (
  shipOrderShippingPoint: ShipOrderShippingPoint,
): ((existingStop: RoutePlanStop) => boolean) => {
  return (existingStop: RoutePlanStop) => {
    const haveSameStopType =
      existingStop.stopType === shipOrderShippingPoint.stopType;

    const haveSameAddress = existingStop.location.hasSameAddress(
      shipOrderShippingPoint.location,
    );

    const haveOverlappingDates =
      existingStop.requestedAppointmentRange.isOverlappedBy(
        shipOrderShippingPoint.requestedAppointment,
      );

    return (
      haveSameStopType &&
      (existingStop.isPlaceholderStop ||
        (haveSameAddress && haveOverlappingDates))
    );
  };
};

/**
 * Generate a predicate similar to
 * `byStopsThatCouldAlmostFulfillShipOrderShippingPoint` but minus the address
 * constraint.
 *
 * @param shipOrderShippingPoint
 * @returns a predicate that can be used in `Array.filter` to find stops that can fulfill the given `ShipOrderShippingPoint`
 */
const stopsWithTypeAndOverlappingDates = (
  shipOrderShippingPoint: ShipOrderShippingPoint,
): ((existingStop: RoutePlanStop) => boolean) => {
  return (existingStop: RoutePlanStop) => {
    const haveSameStopType =
      existingStop.stopType === shipOrderShippingPoint.stopType;

    const haveOverlappingDates =
      existingStop.requestedAppointmentRange.isOverlappedBy(
        shipOrderShippingPoint.requestedAppointment,
      );

    return (
      haveSameStopType &&
      (existingStop.isPlaceholderStop || haveOverlappingDates)
    );
  };
};

export class RoutePlanProposedShipment {
  static readonly MAX_WEIGHT_LBS = 45000;
  static readonly MAX_PALLET_COUNT = 30;
  static readonly MAX_LINEAR_FEET = 53;
  static readonly MAX_VOLUME_CUBIC_FEET = 3800;

  owner?: DomainReference;
  /** DB id of the live shipment this was reverse-mapped from; unset while uncommitted. */
  committedShipmentId?: number;
  stops: RoutePlanStop[] = [];
  transportSegments: RoutePlanTransportSegment[] = [];

  readonly validationErrors: string[] = [];
  readonly validationWarnings: string[] = [];

  _shipmentIdentifier: number;

  constructor(opts?: Partial<RoutePlanProposedShipment>) {
    const identifier = Math.max(
      opts?._shipmentIdentifier ?? opts?.shipmentIdentifier ?? 1,
      1,
    );
    this._shipmentIdentifier = identifier;

    if (opts?.owner) {
      this.owner = new DomainReference(opts.owner);
    }
    this.committedShipmentId = opts?.committedShipmentId;
    if (opts?.stops) {
      this.stops = opts.stops.map(
        (stop) =>
          new RoutePlanStop({
            ...stop,
            shipmentIdentifier: identifier,
          }),
      );
    }

    if (opts?.transportSegments) {
      this.transportSegments = opts.transportSegments.map(
        (t) => new RoutePlanTransportSegment(t),
      );
    }

    if (this.stops.length) {
      this.updateStopSequence();
    }
  }

  static isRoutePlanProposedShipment(
    shipment: RoutePlanProposedShipment | ProposedShipment,
  ): shipment is RoutePlanProposedShipment {
    return 'transportSegments' in shipment;
  }

  static fromShipOrder(identifier: number, shipOrder: ValidShipOrder) {
    const origin = new RoutePlanStop({
      shipmentIdentifier: identifier,
      sequenceNumber: 1,
      stopType: ShipmentStopType.PICKUP,
      location: shipOrder.origin.location,
      requestedAppointmentRange: shipOrder.origin.requestedAppointment,
      shipOrderShippingPointIds: [shipOrder.origin.id],
    });

    const destination = new RoutePlanStop({
      shipmentIdentifier: identifier,
      sequenceNumber: 2,
      stopType: ShipmentStopType.DROP_OFF,
      location: shipOrder.destination.location,
      shipOrderShippingPointIds: [shipOrder.destination.id],
    });

    const transportSegment = new RoutePlanTransportSegment({
      shipOrder,
      pickupStopSequenceNumber: 1,
      dropOffStopSequenceNumber: 2,
    });

    shipOrder.origin.planningStatus = PlanningStatus.PLANNED;
    shipOrder.destination.planningStatus = PlanningStatus.PLANNED;

    return new RoutePlanProposedShipment({
      shipmentIdentifier: identifier,
      stops: [origin, destination],
      transportSegments: [transportSegment],
    });
  }

  /**
   * An identifier for this shipment
   *
   * _Note:_ This is **not** an id but kinda works like one. It should be unique
   * (within a route plan) and unchanging but this model isn't persisted so
   * it's not a primary key to anything.
   **/
  get shipmentIdentifier() {
    return this._shipmentIdentifier;
  }

  set shipmentIdentifier(shipmentIdentifier: number) {
    if (shipmentIdentifier < 1) {
      throw new Error('`shipmentIdentifier` must be positive');
    }

    this._shipmentIdentifier = shipmentIdentifier;
    this.stops.forEach((s) => {
      s.shipmentIdentifier = shipmentIdentifier;
    });
  }

  get identifier() {
    return this.shipmentIdentifier;
  }

  get isEmpty(): boolean {
    return !this.transportSegments.length && !this.origin && !this.destination;
  }

  get isHazmat(): boolean {
    return this.lineItems.some((li) => li.isHazmat);
  }

  get shipOrderIds(): number[] {
    return this.transportSegments.map((ts) => ts.shipOrderId);
  }

  get lineItems(): ProposedShipmentLineItem[] {
    const lineItems: ProposedShipmentLineItem[] = [];

    for (const transportSegment of this.transportSegments) {
      const { pickupStopSequenceNumber, dropOffStopSequenceNumber } =
        transportSegment;
      const shipOrderLineItems = transportSegment.shipOrder?.lineItems ?? [];
      for (const soli of shipOrderLineItems) {
        if (!soli.id) {
          throw new Error(
            'Invalid State - could not determine shiporder line item id',
          );
        }

        const proposedShipmentLineItem = new ProposedShipmentLineItem({
          ...soli,
          shipOrderId: transportSegment.shipOrder!.id,
          shipOrderLineItemId: soli.id,
          pickupStopSequenceNumber,
          dropOffStopSequenceNumber,
        });

        lineItems.push(proposedShipmentLineItem);
      }
    }

    return lineItems;
  }

  get origin(): Optional<RoutePlanStop> {
    const stop = this.stops[0];
    return stop.stopType === ShipmentStopType.PICKUP ? stop : undefined;
  }

  get destination(): Optional<RoutePlanStop> {
    const stop = this.stops[this.stops.length - 1];
    return stop.stopType === ShipmentStopType.DROP_OFF ? stop : undefined;
  }

  get isExistingShipment() {
    return this.transportSegments
      .map((ts) => ts.shipOrder)
      .filter(notNull)
      .some((s) => !!s.shipmentId);
  }

  get requestedAccessorials(): AccessorialCode[] {
    return [];
  }

  /** A unique hex code for the shipment, including the `#` */
  get color() {
    const numColorOptions = Shipment.colorOptions.length;
    const index = (this.shipmentIdentifier - 1) % numColorOptions;
    return Shipment.colorOptions[index];
  }

  getLineItemsForStop(stop: ShippingPoint): ProposedShipmentLineItem[] {
    if (!RoutePlanStop.isRoutePlanStop(stop)) {
      throw new Error('stop should be a RoutePlanStop');
    }

    const isPickup = stop.stopType === ShipmentStopType.PICKUP;
    const seqNumKey = isPickup
      ? 'pickupStopSequenceNumber'
      : 'dropOffStopSequenceNumber';
    return this.lineItems.filter((l) => l[seqNumKey] === stop.sequenceNumber);
  }

  hasShipOrder(shipOrder: ShipOrder): boolean {
    return !!shipOrder.id && this.shipOrderIds.includes(shipOrder.id);
  }

  getStopsForShipOrderShippingPoint(
    shipOrderShippingPoint: ShipOrderShippingPoint,
    shipOrder: ShipOrder,
  ) {
    const transportSegment = this.transportSegments.find(
      (ts) => ts.shipOrderId === shipOrder.id,
    );
    if (!transportSegment) {
      return [];
    }

    // There is a transportSegment with `shipOrder` on this shipment
    // Check if `shipOrderShippingPoint` (`shipOrder`'s origin or destination)
    // is on the shipment.

    const matchingStops = this.stops.filter(
      byStopsThatCouldFulfillShipOrderShippingPoint(shipOrderShippingPoint),
    );

    const sosp = new ValidShipOrderShippingPoint(shipOrderShippingPoint);
    return matchingStops.filter((stop) =>
      stop.shipOrderShippingPointIds.includes(sosp.id),
    );
  }

  hasShipOrderShippingPoint(
    shipOrderShippingPoint: ShipOrderShippingPoint,
    shipOrder: ShipOrder,
  ): boolean {
    const stops = this.getStopsForShipOrderShippingPoint(
      shipOrderShippingPoint,
      shipOrder,
    );
    return !!stops.length;
  }

  hasOriginStop(shipOrder: ShipOrder): boolean {
    const originLocation = shipOrder.origin?.location;
    return this.stops.some((s) => s.location.hasSameAddress(originLocation));
  }

  hasDestinationStop(shipOrder: ShipOrder): boolean {
    const destinationLocation = shipOrder.destination?.location;
    return this.stops.some((s) =>
      s.location.hasSameAddress(destinationLocation),
    );
  }

  hasBothStops(shipOrder: ShipOrder): boolean {
    return this.hasOriginStop(shipOrder) && this.hasDestinationStop(shipOrder);
  }

  findOrCreateTransportSegment(shipOrder: ValidShipOrder): {
    transportSegment: RoutePlanTransportSegment;
    isExisting: boolean;
  } {
    const isExisting = this.shipOrderIds.includes(shipOrder.id);

    // `shipOrderIds` is derived from `transportSegments` so the guard guarantees
    // the `find` will return a transport segment
    const transportSegment = isExisting
      ? this.transportSegments.find((ts) => ts.shipOrderId === shipOrder.id)!
      : new RoutePlanTransportSegment({ shipOrder });

    return {
      transportSegment,
      isExisting,
    };
  }

  addShipOrder(shipOrder: ValidShipOrder): void {
    const { transportSegment, isExisting } =
      this.findOrCreateTransportSegment(shipOrder);
    if (!isExisting) {
      this.transportSegments.push(transportSegment);
    }

    const originStop = this.addShipOrderShippingPoint(shipOrder.origin);
    transportSegment.pickupStopSequenceNumber = originStop.sequenceNumber;

    const { stop: stopForPrereq } = this.findStopByShipOrderShippingPoint(
      shipOrder.origin,
    );
    const destinationStop = this.addShipOrderShippingPoint(
      shipOrder.destination,
      stopForPrereq,
    );
    transportSegment.dropOffStopSequenceNumber = destinationStop.sequenceNumber;
  }

  addPartialShipOrder(shipOrder: ValidShipOrder): void {
    const { transportSegment, isExisting } =
      this.findOrCreateTransportSegment(shipOrder);
    if (!isExisting) {
      this.transportSegments.push(transportSegment);
    }

    if (this.hasOriginStop(shipOrder)) {
      // This shipment has the origin stop, so point the transport segment to it
      const originStop = this.addShipOrderShippingPoint(shipOrder.origin);
      transportSegment.pickupStopSequenceNumber = originStop.sequenceNumber;

      // This shipment doesn't have the destination stop, so find an appropriate
      // stop to point the transport segment to
      const possibleDestinationStops = this.stops.filter(
        (s) => s.sequenceNumber > originStop.sequenceNumber,
      );
      const destinationStop = possibleDestinationStops.find(
        stopsWithTypeAndOverlappingDates(shipOrder.destination),
      );
      transportSegment.dropOffStopSequenceNumber =
        destinationStop?.sequenceNumber;
    }

    if (this.hasDestinationStop(shipOrder)) {
      // This shipment has the destination stop, so point the transport segment
      // to it
      const destinationStop = this.addShipOrderShippingPoint(
        shipOrder.destination,
        this.origin,
      );
      transportSegment.dropOffStopSequenceNumber =
        destinationStop.sequenceNumber;

      // This shipment doesn't have the origin stop, so find an appropriate stop
      // to point the transport segment to
      const possibleOriginStops = this.stops.filter(
        (s) => s.sequenceNumber < destinationStop.sequenceNumber,
      );
      const originStop = possibleOriginStops.find(
        stopsWithTypeAndOverlappingDates(shipOrder.origin),
      );
      transportSegment.pickupStopSequenceNumber = originStop?.sequenceNumber;
    }

    // If the shipment's stops don't fullfil the ship order's origin or destination, assume the ship order is being added at the first stop and removed at the last drop.
    // The ship order is just passing through: on at the first stop, off at the last.
    transportSegment.pickupStopSequenceNumber ??= this.origin?.sequenceNumber;
    transportSegment.dropOffStopSequenceNumber ??=
      this.destination?.sequenceNumber;

    // womp womp
    if (!transportSegment.pickupStopSequenceNumber) {
      console.warn(
        'Could not determine pickup stop sequence number for transport segment',
      );
    }
    if (!transportSegment.dropOffStopSequenceNumber) {
      console.warn(
        'Could not determine dropoff stop sequence number for transport segment',
      );
    }
  }

  addShipOrderShippingPoint(
    shipOrderShippingPoint: ValidShipOrderShippingPoint,
    stopForPrereq?: RoutePlanStop,
  ) {
    // only consider stops that happen after milestones are fulfilled
    // (i.e. dropoffs can't happen before pickup)
    let stopsAfterPrereq: RoutePlanStop[] = this.stops;

    if (stopForPrereq) {
      stopsAfterPrereq = stopsAfterPrereq.filter(
        (s) => s.sequenceNumber > stopForPrereq.sequenceNumber,
      );
    }

    stopsAfterPrereq = [...stopsAfterPrereq].sort(
      (a, b) => a.sequenceNumber - b.sequenceNumber,
    );

    // Find possible merge opportunities
    const stopToCombineWith = stopsAfterPrereq.find(
      byStopsThatCouldFulfillShipOrderShippingPoint(shipOrderShippingPoint),
    );

    let stopForShipOrderShippingPoint: RoutePlanStop;
    if (stopToCombineWith) {
      stopToCombineWith.addShipOrderShippingPoint(shipOrderShippingPoint);
      stopToCombineWith._requestedAppointmentRange =
        stopToCombineWith._requestedAppointmentRange
          ? stopToCombineWith._requestedAppointmentRange.intersectionWith(
              shipOrderShippingPoint.requestedAppointment,
            )
          : shipOrderShippingPoint.requestedAppointment;
      stopForShipOrderShippingPoint = stopToCombineWith;

      if (stopForShipOrderShippingPoint.isPlaceholderStop) {
        stopForShipOrderShippingPoint.location =
          shipOrderShippingPoint.location;
      }
    } else {
      let indexToInsert = this.stops.length;

      for (const existingStop of stopsAfterPrereq) {
        const existingApptRange = existingStop.requestedAppointmentRange;
        const newApptRange = shipOrderShippingPoint.requestedAppointment;

        const isExistingRangeCompletelyAfterNewRange =
          !!newApptRange.end && existingApptRange.isAfterRange(newApptRange);

        const isExistingRangePartiallyAfterNewRange =
          !!newApptRange.start &&
          !!existingApptRange.start &&
          newApptRange.start < existingApptRange.start;

        const shouldGoBeforeExistingStop =
          existingApptRange.isEmpty ||
          isExistingRangeCompletelyAfterNewRange ||
          isExistingRangePartiallyAfterNewRange;

        if (shouldGoBeforeExistingStop) {
          indexToInsert = this.stops.indexOf(existingStop);
          break;
        }
      }

      // create new stop
      const newStop = new RoutePlanStop({
        shipmentIdentifier: this.identifier,
        sequenceNumber: -1,
        stopType: shipOrderShippingPoint.stopType,
        location: shipOrderShippingPoint.location,
        requestedAppointmentRange: shipOrderShippingPoint.requestedAppointment,
        shipOrderShippingPointIds: [shipOrderShippingPoint.id],
      });

      // insert at the proper position
      this.stops.splice(indexToInsert, 0, newStop);
      this.updateStopSequence();
      stopForShipOrderShippingPoint = newStop;
    }

    shipOrderShippingPoint.planningStatus = PlanningStatus.PLANNED;
    return stopForShipOrderShippingPoint;
  }

  removeShipOrder(
    shipOrder: ShipOrder,
    options: typeof DEFAULT_REMOVE_SHIP_ORDER_OPTIONS = DEFAULT_REMOVE_SHIP_ORDER_OPTIONS,
  ): void {
    const opts = { ...DEFAULT_REMOVE_SHIP_ORDER_OPTIONS, options };
    const localTransportSegmentIndex = this.transportSegments.findIndex(
      (ts) => ts.shipOrderId === shipOrder.id,
    );
    if (localTransportSegmentIndex === -1) {
      console.warn(
        'Could not find transport segment to remove for ship order ',
        shipOrder,
      );
      return;
    }

    const [removedTransportSegment] = this.transportSegments.splice(
      localTransportSegmentIndex,
      1,
    );

    if (opts.removeAssociatedStops) {
      const { pickupStopSequenceNumber, dropOffStopSequenceNumber } =
        removedTransportSegment;

      const isPickupStillValid = this.transportSegments.some(
        (ts) => ts.pickupStopSequenceNumber === pickupStopSequenceNumber,
      );
      if (!isPickupStillValid && pickupStopSequenceNumber) {
        this.removeStop(pickupStopSequenceNumber);
      }

      const isDropoffStillValid = this.transportSegments.some(
        (ts) => ts.dropOffStopSequenceNumber === dropOffStopSequenceNumber,
      );
      if (!isDropoffStillValid && dropOffStopSequenceNumber) {
        this.removeStop(dropOffStopSequenceNumber);
      }
    }
  }

  validateStopResequencing(
    oldSequenceNumber: number,
    newSequenceNumber: number,
  ): ResequenceState {
    const stop = this.stops.find((s) => s.sequenceNumber === oldSequenceNumber);
    if (!stop) {
      return {
        canResequence: false,
        canResequenceReason: i18n.global.t(
          'multistop.cantResequenceReason.doesNotExist',
        ),
      };
    }

    const prevStop =
      oldSequenceNumber > 1
        ? this.stops.find((s) => s.sequenceNumber === oldSequenceNumber - 1)
        : undefined;
    const nextStop =
      oldSequenceNumber < this.stops.length
        ? this.stops.find((s) => s.sequenceNumber === oldSequenceNumber + 1)
        : undefined;
    const stopType = stop.stopType;
    if (
      (stopType === ShipmentStopType.PICKUP &&
        newSequenceNumber === this.stops.length) ||
      (stopType === ShipmentStopType.DROP_OFF &&
        oldSequenceNumber === this.stops.length &&
        prevStop?.stopType === ShipmentStopType.PICKUP)
    ) {
      return {
        canResequence: false,
        canResequenceReason: i18n.global.t(
          'multistop.cantResequenceReason.lastStop',
        ),
      };
    } else if (
      (stopType === ShipmentStopType.DROP_OFF && newSequenceNumber === 1) ||
      (stopType === ShipmentStopType.PICKUP &&
        oldSequenceNumber === 1 &&
        nextStop?.stopType === ShipmentStopType.DROP_OFF)
    ) {
      return {
        canResequence: false,
        canResequenceReason: i18n.global.t(
          'multistop.cantResequenceReason.firstStop',
        ),
      };
    }

    // line items being dropped off at this stop
    const lineItemsBeingDroppedOff = this.lineItems.filter(
      (li) => li.dropOffStopSequenceNumber === oldSequenceNumber,
    );
    // the pickup stops for all the line items that are being dropped off at this stop
    const relatedPickupStops = this.stops.filter((stop) =>
      lineItemsBeingDroppedOff.some(
        (li) => li.pickupStopSequenceNumber === stop.sequenceNumber,
      ),
    );
    // the highest sequence number of all stops that are pickups for this drop
    // the stop being modified cannot be moved before this sequence number
    const minSeqNum = relatedPickupStops
      .map((stop) => stop.sequenceNumber)
      .reduce((a, b) => Math.max(a, b), -1);

    // line items being picked up at this stop
    const lineItemsBeingPickedUp = this.lineItems.filter(
      (li) => li.pickupStopSequenceNumber === oldSequenceNumber,
    );
    // the drop off stops for all the line items that are being picked up at this stop
    const relatedDropoffStops = this.stops.filter((stop) =>
      lineItemsBeingPickedUp.some(
        (li) => li.dropOffStopSequenceNumber === stop.sequenceNumber,
      ),
    );
    // the lowest sequence number of all stops that are dropoffs for this pickup
    // the stop being modified cannot be moved after this sequence number
    const maxSeqNum = relatedDropoffStops
      .map((stop) => stop.sequenceNumber)
      .reduce((a, b) => Math.min(a, b), this.stops.length + 1);

    if (newSequenceNumber <= minSeqNum) {
      return {
        canResequence: false,
        canResequenceReason: i18n.global.t(
          'multistop.cantResequenceReason.beforeRelatedPickup',
        ),
      };
    } else if (newSequenceNumber >= maxSeqNum) {
      return {
        canResequence: false,
        canResequenceReason: i18n.global.t(
          'multistop.cantResequenceReason.afterRelatedDropoff',
        ),
      };
    } else {
      return {
        canResequence: true,
        canResequenceReason: '',
      };
    }
  }

  resequenceStop(oldSequenceNumber: number, newSequenceNumber: number): void {
    const { canResequence, canResequenceReason } =
      this.validateStopResequencing(oldSequenceNumber, newSequenceNumber);

    if (!canResequence) {
      throw new Error(canResequenceReason);
    }

    const stopOldIndex = this.stops.findIndex(
      (s) => s.sequenceNumber === oldSequenceNumber,
    );
    if (stopOldIndex === -1) {
      throw new Error(
        `Could not find stop with sequenceNumber ${oldSequenceNumber}`,
      );
    }
    const stopNewIndex = newSequenceNumber - 1;

    // Capture which stops are associated with each transport segment so that
    // pickup and dropoff sequence numbers can be corrected after re-ordering
    const tsToStopsEntries = this.transportSegments.map((ts) => {
      const pickupStop = this.stops.find(
        (s) => s.sequenceNumber === ts.pickupStopSequenceNumber,
      );
      const dropOffStop = this.stops.find(
        (s) => s.sequenceNumber === ts.dropOffStopSequenceNumber,
      );
      return [ts, [pickupStop, dropOffStop]] as const;
    });
    const tsToStopsMap = new Map(tsToStopsEntries);

    const stops = [...this.stops];
    const [stop] = stops.splice(stopOldIndex, 1);
    stops.splice(stopNewIndex, 0, stop);
    for (let i = 0; i < stops.length; i++) {
      stops[i].sequenceNumber = i + 1;
    }
    this.stops.sort((a, b) => a.sequenceNumber - b.sequenceNumber);

    // Update transport segments with new sequence numbers
    this.transportSegments.forEach((ts) => {
      const [pickupStop, dropOffStop] = tsToStopsMap.get(ts) ?? [];
      ts.pickupStopSequenceNumber = pickupStop?.sequenceNumber;
      ts.dropOffStopSequenceNumber = dropOffStop?.sequenceNumber;
    });
  }

  findStopByShipOrderShippingPoint(
    shipOrderShippingPoint?: ValidShipOrderShippingPoint,
  ): { stopIndex: number; stop?: RoutePlanStop } {
    let stopIndex = -1;

    const shipOrderShippingPointId = shipOrderShippingPoint?.id;
    if (shipOrderShippingPointId) {
      stopIndex = this.stops.findIndex((stop) =>
        stop.shipOrderShippingPointIds.includes(shipOrderShippingPointId),
      );
    }

    const stop: RoutePlanStop | undefined =
      stopIndex !== -1 ? this.stops[stopIndex] : undefined;

    return { stopIndex, stop };
  }

  addStop({ location, stopType }: AddStopToShipmentPayload) {
    const newStop = new RoutePlanStop({
      shipmentIdentifier: this.shipmentIdentifier,
      sequenceNumber: -1,
      stopType,
      location,
    });

    // The new stop is added right before the destination
    const index = this.stops.length - 1;
    this.stops.splice(index, 0, newStop);
    this.updateStopSequence();
  }

  removeStop(sequenceNumber: number): Optional<RoutePlanStop> {
    const stopIndex = this.stops.findIndex(
      (s) => s.sequenceNumber === sequenceNumber,
    );
    if (stopIndex === -1) {
      console.warn(`Could not find stop with sequenceNumber ${sequenceNumber}`);
      return;
    }

    // For origin/destination, we don't actually remove the stop. We remove the
    // location and ship orders from the stop, but leave the empty stop
    // as a placeholder
    let removedStop: RoutePlanStop;
    if (sequenceNumber === 1 || sequenceNumber === this.stops.length) {
      removedStop = this.stops[stopIndex];
      removedStop.location = undefined;
    } else {
      [removedStop] = this.stops.splice(stopIndex, 1);
    }

    this.updateStopSequence();
    return removedStop;
  }

  updateStopSequence(): void {
    for (let i = 0; i < this.stops.length; i++) {
      const stop = this.stops[i];
      const oldSequenceNumber = stop.sequenceNumber;
      const newSequenceNumber = i + 1;
      const hasMoved = oldSequenceNumber !== newSequenceNumber;

      stop.sequenceNumber = newSequenceNumber;
      if (hasMoved) {
        this.transportSegments.forEach((ts) => {
          if (ts.pickupStopSequenceNumber === oldSequenceNumber) {
            ts.pickupStopSequenceNumber = newSequenceNumber;
          }

          if (ts.dropOffStopSequenceNumber === oldSequenceNumber) {
            ts.dropOffStopSequenceNumber = newSequenceNumber;
          }
        });
      }
    }
  }

  toJSON() {
    // `JSON.stringify` ignores getters so manually add them here if needed
    return {
      identifier: this.shipmentIdentifier,
      shipmentIdentifier: this.shipmentIdentifier,
      committedShipmentId: this.committedShipmentId,
      shipOrderIds: this.shipOrderIds,
      stops: this.stops,
      transportSegments: this.transportSegments,
      lineItems: this.lineItems,
    };
  }
}
